import { forwardRef, type ButtonHTMLAttributes, type MouseEvent } from 'react'

/**
 * Accessible on/off switch (`<button role="switch">`; the app has no switch primitive - checkboxes
 * were the previous idiom). Styling lives in global.css (`.switch`); checked state is carried by
 * aria-checked so the visual and the a11y state can't drift. Clicks stop propagation because the
 * switch lives inside `row-link` table rows whose row-level onClick navigates. Ref + unknown props
 * are forwarded onto the button so a `<Tooltip>` can clone its hover/focus handlers onto it.
 */
export interface SwitchProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> {
  checked: boolean
  onChange: (next: boolean) => void
  /** Accessible name - the switch renders no visible text. */
  label: string
  /** A change is in flight; renders as disabled without flipping the visual state early. */
  busy?: boolean
}

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { checked, onChange, label, disabled, busy, onClick, ...rest },
  ref,
) {
  function handleClick(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation()
    onClick?.(e)
    onChange(!checked)
  }
  return (
    <button
      {...rest}
      ref={ref}
      type="button"
      role="switch"
      className="switch"
      aria-checked={checked}
      aria-label={label}
      aria-busy={busy || undefined}
      disabled={disabled || busy}
      onClick={handleClick}
    />
  )
})
